import { DndContext, DragEndEvent, DragOverEvent, DragOverlay, DragStartEvent, closestCenter, closestCorners, useDroppable } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { List } from "@mui/material";
import { useEffect, useState, Fragment } from 'react';
import MyListItem from "./MyListItem";
import MySortableListItem from './MySortableListItem';
import { useDefaultSensors } from './presets';
import { iKanbanBoard, iKanbanItem } from './types';

interface KanbanProps {
  items: Array<iKanbanItem>,
  boards: Array<iKanbanBoard>,
}

interface Card extends iKanbanBoard {
  cards: Array<iKanbanItem>,
}

// @see https://codesandbox.io/p/sandbox/dnd-kit-kanban-board-1df69n?file=%2Fsrc%2FApp.tsx%3A78%2C42
export default function({ items: pItems, boards }: KanbanProps) {
  const sensors = useDefaultSensors();
  const [columns, setColumns] = useState<Card[]>([]);
  const [items, setItems] = useState(pItems);

  useEffect(() => {
    let itemsByBoardId = Object.groupBy(items, item => item.board_id);
    let c: Card[] = boards.map(x => ({ ...x, cards: itemsByBoardId[x.id] ?? [] }));
    console.log(c);
    setColumns(c);
  }, [items, boards]);

  const findColumn = (unique: number | null): Card | null => {
    if (!unique) {
      return null;
    }
    if (columns.some((c) => c.id === unique)) {
      return columns.find((c) => c.id === unique) ?? null;
    }
    const id = unique;
    console.log('findColumn', columns, unique);
    const itemWithColumnId = columns.flatMap((c) => {
      const columnId = c.id;
      return c.cards.map((i) => (i ? { id: i.id, board_id: columnId } : i));
    });
    const columnId = itemWithColumnId.find((i) => i ? (i.id === id) : false) ?.board_id;
    return columns.find((c) => c.id === columnId) ?? null;
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over, delta } = event;
    const activeId = active.id as number;
    const overId = over ? over.id as number : null;
    const activeColumn = findColumn(activeId);
    const overColumn = findColumn(overId);
    if (!activeColumn || !overColumn || activeColumn === overColumn) {
      return null;
    }
    setColumns((prevState) => {
      const activeItems = activeColumn.cards;
      const overItems = overColumn.cards;
      const activeIndex = activeItems.findIndex((i) => i ? i.id === activeId : false);
      const overIndex = overItems.findIndex((i) => i ? i.id === overId : false);
      const newIndex = () => {
        const putOnBelowLastItem =
          overIndex === overItems.length - 1 && delta.y > 0;
        const modifier = putOnBelowLastItem ? 1 : 0;
        return overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
      };
      return prevState.map((c) => {
        if (c.id === activeColumn.id) {
          c.cards = activeItems.filter((i) => i ? (i.id !== activeId) : false);
          return c;
        } else if (c.id === overColumn.id) {
          c.cards = [
            ...overItems.slice(0, newIndex()),
            activeItems[activeIndex],
            ...overItems.slice(newIndex(), overItems.length)
          ];
          return c;
        } else {
          return c;
        }
      });
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const activeId = active.id as number;
    const overId = over ? over.id as number : null;
    const activeColumn = findColumn(activeId);
    const overColumn = findColumn(overId);
    if (!activeColumn || !overColumn || activeColumn !== overColumn) {
      return null;
    }
    const activeIndex = activeColumn.cards.findIndex((i) => i ? i.id === activeId : false);
    const overIndex = overColumn.cards.findIndex((i) => i ? i.id === overId : false);
    if (activeIndex !== overIndex) {
      setColumns((prevState) => {
        return prevState.map((column) => {
          if (column.id === activeColumn.id) {
            column.cards = arrayMove(overColumn.cards, activeIndex, overIndex);
            return column;
          } else {
            return column;
          }
        });
      });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <div style={{ display: "flex", flexDirection: "row" }}>
        {columns.map((column) => <KanbanColumn  key={column.id} column={column}/>)}
      </div>
      {/* {handle.boards.map(board => {
        const sHandle = useSortableHandle({ items: handle.items.filter(x => board.id === x.board_id) });
        return <Sortable key={board.id} id={board.id} handle={sHandle} strategy={rectSortingStrategy} />
      })} */}
    </DndContext>
  )
}

interface KanbanColumnProps {
  column: Card,
}

function KanbanColumn({column}: KanbanColumnProps) {
  const { setNodeRef } = useDroppable({ id: column.id });
  return <SortableContext
    id={String(column.id)}
    items={column.cards}
    strategy={rectSortingStrategy}
  >
    <div>
      <h3>{column.title}</h3>
      <List ref={setNodeRef}>
        {column.cards.map(item => (item ? <MySortableListItem key={item.id} item={item} /> : null))}
      </List>
    </div>
  </SortableContext>
}
